import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { PbrMaterial } from "../../core/mdl/PbrMaterial";
import { AbstractTool } from "../../core/tool/AbstractTool";
import { Interactor as CoreInteractor, InteractorType } from "../../core/tool/Interactor";
import { InteractorManager } from "../../core/tool/InteractorManager";
import { VECTOR3_X, VECTOR3_Y, VECTOR3_Z } from "../../core/utils/MathUtils";
import { setLocalMatrix } from "../../core/utils/MeshUtils";
import { CameraMode } from "../../core/view/CameraControl";
import { compare } from "../../utils/string";
import { DimChangeTool } from "./DimChangeTool";
import { getSpriteUrl } from "./InteractorSprites";
/**
 * Note: Because BabylonJS is using a left handed coordinate sytem we have to invert z-axis.
 * That means the meaning of `NZ` and `PZ` is inverted.
 */
export class DimChangeMode extends AbstractTool {
    constructor(pApp, pContext, pDimChanges) {
        super(pApp);
        this.onSelectionChanged = () => {
            this.stop();
        };
        this.onCameraChanged = () => {
            this.createOrUpdateInteractors();
        };
        this.onArticleChanged = () => {
            this.stop();
        };
        this.mView = pApp.viewer.view;
        this.mInteractors = new InteractorManager(pApp.viewer);
        this.mContext = pContext;
        this.mArticle = pContext.article;
        this.mArticleBounds = this.mArticle.boundingBox.clone();
        this.mDimChanges = this.getSortedDimChanges(pDimChanges);
        this.mActiveDimChanges = [];
        this.mFeedbackNode = null;
        this.mFeedbackMat = null;
    }
    start(pParameter) {
        if (this.mDimChanges.length === 0)
            return (false);
        this.mInteractors.enabled = true;
        this.createOrUpdateInteractors();
        this.createFeedback();
        this.mApp.model.eventSelectionChanged.addListener(this.onSelectionChanged);
        this.mView.cameraControl.eventCameraChanged.addListener(this.onCameraChanged);
        this.mArticle.getMainArticle().eventGeometryChanged.addListener(this.onArticleChanged);
        return (super.start(pParameter));
    }
    stop() {
        this.mApp.model.eventSelectionChanged.removeListener(this.onSelectionChanged);
        this.mView.cameraControl.eventCameraChanged.removeListener(this.onCameraChanged);
        this.mArticle.getMainArticle().eventGeometryChanged.removeListener(this.onArticleChanged);
        this.mInteractors.clear();
        this.mInteractors.enabled = false;
        if (this.mFeedbackNode != null) {
            this.mFeedbackNode.dispose(false);
            this.mFeedbackNode = null;
            //this.mFeedbackBox = null;
        }
        if (this.mFeedbackMat != null) {
            this.mFeedbackMat.dispose();
            this.mFeedbackMat = null;
        }
        this.mApp.viewer.requestRenderFrame();
        super.stop();
    }
    static GetAxis(pDim) {
        if (pDim.dimension.endsWith("X"))
            return ("x");
        if (pDim.dimension.endsWith("Y"))
            return ("y");
        return ("z");
    }
    static GetAxisVector(pDim) {
        if (pDim.dimension.endsWith("X"))
            return (VECTOR3_X);
        if (pDim.dimension.endsWith("Y"))
            return (VECTOR3_Y);
        return (VECTOR3_Z);
    }
    static GetContext(pInteractor) {
        //find nearest bounding box edge
        const tPoints = pInteractor.interactors.article.boundingBox.getPoints();
        let tCorner;
        let tMinDist = Number.MAX_VALUE;
        for (let i = 0; i < tPoints.length; ++i) {
            const tDist = Vector3.DistanceSquared(pInteractor.position, tPoints[i]);
            if (tCorner == null || tDist < tMinDist) {
                tCorner = i;
                tMinDist = tDist;
            }
        }
        return ({
            article: pInteractor.interactors.article,
            oapInteractorId: pInteractor.oapInteractor.id,
            finishInteractorCorner: tCorner !== null && tCorner !== void 0 ? tCorner : 0
        });
    }
    static GetDirectionToCamera(pPos, pView) {
        let tCameraVec;
        if (pView.cameraControl.cameraMode === CameraMode.Perspective) {
            tCameraVec = pView.cameraControl.position.subtract(pPos);
            tCameraVec.normalize();
        }
        else
            tCameraVec = pView.cameraControl.direction.negate();
        return (tCameraVec);
    }
    static MayStartDimChangeMode(pArticle, pView, pDimChanges) {
        if (pView == null || pDimChanges == null || pDimChanges.length === 0)
            return (false);
        if (pDimChanges.length > 1) //there should be atleast one usable axis
            return (true);
        const tActive = DimChangeMode.GatActiveDimChanges(pArticle, pView, pDimChanges);
        return (tActive.length > 0);
    }
    /// private //////////////////////////////////////////////////////////////////////////////
    static GatActiveDimChanges(pArticle, pView, pDimChanges) {
        //filter out dim changes that can't be used with the current camera
        const tActive = new Array();
        const tWordlMatrix = pArticle.worldTransform;
        const tWordCenter = Vector3.TransformCoordinates(pArticle.boundingBox.getCenter(), tWordlMatrix);
        const tCamDir = DimChangeMode.GetDirectionToCamera(tWordCenter, pView);
        for (const tDim of pDimChanges) {
            const tWorldAxis = Vector3.TransformNormal(DimChangeMode.GetAxisVector(tDim), tWordlMatrix);
            const tAbsDot = Math.abs(Vector3.Dot(tCamDir, tWorldAxis));
            if (tAbsDot < 0.95) {
                tActive.push(tDim);
            }
        }
        return (tActive);
    }
    createOrUpdateInteractors() {
        if (!this.needInteractorUpdate())
            return;
        this.mInteractors.clear();
        let tDim1;
        let tDim2;
        let tDim3;
        //get interactor order
        for (const tDim of this.mActiveDimChanges) {
            if (tDim3 == null && tDim.thirdDimension && this.mActiveDimChanges.length > 2) {
                tDim3 = tDim;
            }
            else if (tDim1 == null) {
                tDim1 = tDim;
            }
            else if (tDim2 == null) {
                tDim2 = tDim;
            }
        }
        //more than 2 dimension but no 3rd dimension is defined, use last one
        if (tDim3 == null && this.mActiveDimChanges.length > 2) {
            tDim3 = this.mActiveDimChanges[0];
        }
        if (tDim1 == null)
            return; //should not happen
        const tPlane = this.getPlane(tDim1, tDim2, tDim3);
        //create interactors
        let tUseOppositeSide = false;
        this.createFinishInteractor();
        if (tDim3 != null) {
            tUseOppositeSide = tDim3.dimension === "NX" || tDim3.dimension === "NY" || tDim3.dimension === "NZ";
            this.create3rdInteractors(tDim3);
        }
        if (tDim1 != null) {
            this.createSeparateInteractors(tDim1, tPlane, tUseOppositeSide);
        }
        if (tDim2 != null) {
            this.createSeparateInteractors(tDim2, tPlane, tUseOppositeSide);
        }
        if (tDim1 != null && !tDim1.changeSeparately && tDim2 != null && !tDim2.changeSeparately) {
            this.createMultiInteractors(tDim1, tDim2, tPlane, tUseOppositeSide);
        }
    }
    createFeedback() {
        this.mFeedbackMat = new PbrMaterial("dim_feedback_mat", this.mApp.model.materials);
        this.mFeedbackMat.albedoColor = Color3.FromHexString(this.mApp.theme.mainColorBright).toLinearSpace();
        this.mFeedbackMat.roughness = 1;
        this.mFeedbackMat.metallic = 0;
        this.mFeedbackMat.alpha = 0.2;
        const tSize = this.mArticleBounds.getSize();
        this.mFeedbackNode = new TransformNode("dim_feedback_node", this.mApp.model.scene);
        setLocalMatrix(this.mFeedbackNode, this.mArticle.worldTransform);
        const tFeedbackMesh = CreateBox("dim_feedback_mesh", { size: 1, updatable: false }, this.mApp.model.scene);
        tFeedbackMesh.bakeTransformIntoVertices(Matrix.Translation(0.5, 0.5, 0.5));
        tFeedbackMesh.position.copyFrom(this.mArticleBounds.min);
        tFeedbackMesh.scaling.copyFrom(tSize);
        tFeedbackMesh.showBoundingBox = true;
        tFeedbackMesh.parent = this.mFeedbackNode;
        tFeedbackMesh.material = this.mFeedbackMat;
        tFeedbackMesh.renderingGroupId = 1;
        this.mApp.viewer.requestRenderFrame();
    }
    needInteractorUpdate() {
        const tNewActive = DimChangeMode.GatActiveDimChanges(this.mArticle, this.mView, this.mDimChanges);
        //compare current active with new active
        let tEqual = false;
        if (tNewActive.length === this.mActiveDimChanges.length) {
            tEqual = true;
            for (let i = 0; i < tNewActive.length; ++i) {
                if (tNewActive[i] !== this.mActiveDimChanges[i]) {
                    tEqual = false;
                    break;
                }
            }
        }
        if (!tEqual) {
            this.mActiveDimChanges = tNewActive;
            return (true);
        }
        return (false);
    }
    createSeparateInteractors(pDim, pPlane, pUseOppositeSide) {
        const tMin = this.mArticleBounds.min;
        const tMax = this.mArticleBounds.max;
        const tCenter = this.mArticleBounds.getCenter();
        if (pPlane === "xz") {
            const tY = pUseOppositeSide ? tMax.y : tMin.y;
            if (pDim.dimension === "X" || pDim.dimension === "NX") {
                this.createInteractor(new Vector3(tMax.x, tY, tMax.z), new Vector3(tMin.x, tY, tCenter.z), pDim, undefined, pPlane);
            }
            if (pDim.dimension === "X" || pDim.dimension === "PX") {
                this.createInteractor(new Vector3(tMin.x, tY, tMax.z), new Vector3(tMax.x, tY, tCenter.z), pDim, undefined, pPlane);
            }
            if (pDim.dimension === "Z" || pDim.dimension === "NZ") {
                this.createInteractor(new Vector3(tMin.x, tY, tMin.z), new Vector3(tCenter.x, tY, tMax.z), pDim, undefined, pPlane);
            }
            if (pDim.dimension === "Z" || pDim.dimension === "PZ") {
                this.createInteractor(new Vector3(tMin.x, tY, tMax.z), new Vector3(tCenter.x, tY, tMin.z), pDim, undefined, pPlane);
            }
        }
        else if (pPlane === "yz") {
            const tX = pUseOppositeSide ? tMax.x : tMin.x;
            if (pDim.dimension === "Y" || pDim.dimension === "NY") {
                this.createInteractor(new Vector3(tX, tMax.y, tMax.z), new Vector3(tX, tMin.y, tCenter.z), pDim, undefined, pPlane);
            }
            if (pDim.dimension === "Y" || pDim.dimension === "PY") {
                this.createInteractor(new Vector3(tX, tMin.y, tMax.z), new Vector3(tX, tMax.y, tCenter.z), pDim, undefined, pPlane);
            }
            if (pDim.dimension === "Z" || pDim.dimension === "NZ") {
                this.createInteractor(new Vector3(tX, tMin.y, tMin.z), new Vector3(tX, tCenter.y, tMax.z), pDim, undefined, pPlane);
            }
            if (pDim.dimension === "Z" || pDim.dimension === "PZ") {
                this.createInteractor(new Vector3(tX, tMin.y, tMax.z), new Vector3(tX, tCenter.y, tMin.z), pDim, undefined, pPlane);
            }
        }
        else if (pPlane === "xy") {
            const tZ = pUseOppositeSide ? tMin.z : tMax.z;
            if (pDim.dimension === "X" || pDim.dimension === "NX") {
                this.createInteractor(new Vector3(tMax.x, tMin.y, tZ), new Vector3(tMin.x, tCenter.y, tZ), pDim, undefined, pPlane);
            }
            if (pDim.dimension === "X" || pDim.dimension === "PX") {
                this.createInteractor(new Vector3(tMin.x, tMin.y, tZ), new Vector3(tMax.x, tCenter.y, tZ), pDim, undefined, pPlane);
            }
            if (pDim.dimension === "Y" || pDim.dimension === "NY") {
                this.createInteractor(new Vector3(tMin.x, tMax.y, tZ), new Vector3(tCenter.x, tMin.y, tZ), pDim, undefined, pPlane);
            }
            if (pDim.dimension === "Y" || pDim.dimension === "PY") {
                this.createInteractor(new Vector3(tMin.x, tMin.y, tZ), new Vector3(tCenter.x, tMax.y, tZ), pDim, undefined, pPlane);
            }
        }
    }
    create3rdInteractors(pDim) {
        const tMin = this.mArticleBounds.min;
        const tMax = this.mArticleBounds.max;
        const tCenter = this.mArticleBounds.getCenter();
        if (pDim.dimension === "X" || pDim.dimension === "NX") {
            this.createInteractor(new Vector3(tMax.x, tMin.y, tMax.z), new Vector3(tMin.x, tCenter.y, tCenter.z), pDim);
        }
        if (pDim.dimension === "X" || pDim.dimension === "PX") {
            this.createInteractor(new Vector3(tMin.x, tMin.y, tMax.z), new Vector3(tMax.x, tCenter.y, tCenter.z), pDim);
        }
        if (pDim.dimension === "Y" || pDim.dimension === "NY") {
            this.createInteractor(new Vector3(tMin.x, tMax.y, tMax.z), new Vector3(tCenter.x, tMin.y, tCenter.z), pDim);
        }
        if (pDim.dimension === "Y" || pDim.dimension === "PY") {
            this.createInteractor(new Vector3(tMin.x, tMin.y, tMax.z), new Vector3(tCenter.x, tMax.y, tCenter.z), pDim);
        }
        if (pDim.dimension === "Z" || pDim.dimension === "NZ") {
            this.createInteractor(new Vector3(tMin.x, tMin.y, tMin.z), new Vector3(tCenter.x, tCenter.y, tMax.z), pDim);
        }
        if (pDim.dimension === "Z" || pDim.dimension === "PZ") {
            this.createInteractor(new Vector3(tMin.x, tMin.y, tMax.z), new Vector3(tCenter.x, tCenter.y, tMin.z), pDim);
        }
    }
    createMultiInteractors(pDim1, pDim2, pPlane, pUseOppositeSide) {
        const tMin = this.mArticleBounds.min;
        const tMax = this.mArticleBounds.max;
        if (pPlane === "xz") {
            const tY = pUseOppositeSide ? tMax.y : tMin.y;
            if (pDim1.dimension !== "PX" && pDim2.dimension !== "NZ") {
                this.createInteractor(new Vector3(tMax.x, tY, tMax.z), new Vector3(tMin.x, tY, tMin.z), pDim1, pDim2, pPlane);
            }
            if (pDim1.dimension !== "PX" && pDim2.dimension !== "PZ") {
                this.createInteractor(new Vector3(tMax.x, tY, tMin.z), new Vector3(tMin.x, tY, tMax.z), pDim1, pDim2, pPlane);
            }
            if (pDim1.dimension !== "NX" && pDim2.dimension !== "PZ") {
                this.createInteractor(new Vector3(tMin.x, tY, tMin.z), new Vector3(tMax.x, tY, tMax.z), pDim1, pDim2, pPlane);
            }
            if (pDim1.dimension !== "NX" && pDim2.dimension !== "NZ") {
                this.createInteractor(new Vector3(tMin.x, tY, tMax.z), new Vector3(tMax.x, tY, tMin.z), pDim1, pDim2, pPlane);
            }
        }
        else if (pPlane === "yz") {
            const tX = pUseOppositeSide ? tMax.x : tMin.x;
            if (pDim1.dimension !== "PY" && pDim2.dimension !== "NZ") {
                this.createInteractor(new Vector3(tX, tMax.y, tMax.z), new Vector3(tX, tMin.y, tMin.z), pDim1, pDim2, pPlane);
            }
            if (pDim1.dimension !== "PY" && pDim2.dimension !== "PZ") {
                this.createInteractor(new Vector3(tX, tMax.y, tMin.z), new Vector3(tX, tMin.y, tMax.z), pDim1, pDim2, pPlane);
            }
            if (pDim1.dimension !== "NY" && pDim2.dimension !== "PZ") {
                this.createInteractor(new Vector3(tX, tMin.y, tMin.z), new Vector3(tX, tMax.y, tMax.z), pDim1, pDim2, pPlane);
            }
            if (pDim1.dimension !== "NY" && pDim2.dimension !== "NZ") {
                this.createInteractor(new Vector3(tX, tMin.y, tMax.z), new Vector3(tX, tMax.y, tMin.z), pDim1, pDim2, pPlane);
            }
        }
        else if (pPlane === "xy") {
            const tZ = pUseOppositeSide ? tMin.z : tMax.z;
            if (pDim1.dimension !== "PX" && pDim2.dimension !== "NY") {
                this.createInteractor(new Vector3(tMax.x, tMin.y, tZ), new Vector3(tMin.x, tMax.y, tZ), pDim1, pDim2, pPlane);
            }
            if (pDim1.dimension !== "PX" && pDim2.dimension !== "PY") {
                this.createInteractor(new Vector3(tMax.x, tMax.y, tZ), new Vector3(tMin.x, tMin.y, tZ), pDim1, pDim2, pPlane);
            }
            if (pDim1.dimension !== "NX" && pDim2.dimension !== "PY") {
                this.createInteractor(new Vector3(tMin.x, tMax.y, tZ), new Vector3(tMax.x, tMin.y, tZ), pDim1, pDim2, pPlane);
            }
            if (pDim1.dimension !== "NX" && pDim2.dimension !== "NY") {
                this.createInteractor(new Vector3(tMin.x, tMin.y, tZ), new Vector3(tMax.x, tMax.y, tZ), pDim1, pDim2, pPlane);
            }
        }
    }
    createFinishInteractor() {
        const tDiv = document.createElement("div");
        const tSpan = document.createElement("span");
        tSpan.style.backgroundImage = "url('" + getSpriteUrl() + "')";
        tDiv.appendChild(tSpan);
        tDiv.className = "wcf-cf oap-interactor-large-finish";
        const tPos = this.mArticleBounds.getPoints()[this.mContext.finishInteractorCorner];
        const tInteractor = new CoreInteractor(tDiv, this.mApp.viewer);
        tInteractor.type = InteractorType.Button;
        tInteractor.worldMatrix = this.mArticle.worldTransform;
        tInteractor.position = tPos;
        tInteractor.posOffset = 40;
        tInteractor.posOffsetDirection = tPos.subtract(this.mArticleBounds.getCenter()).normalize();
        tInteractor.customCssTransform = "translate(-50%, -50%)";
        tInteractor.onTriggerAction = () => {
            this.stop();
        };
        this.mInteractors.addInteractor(tInteractor);
    }
    createInteractor(pBBOrigin, pPos, pDimChange1, pDimChange2, pPlane) {
        const tDiv = document.createElement("div");
        const tSpan = document.createElement("span");
        tDiv.appendChild(tSpan);
        tDiv.className = "wcf-cf dim-change-interactor";
        const tInteractor = new CoreInteractor(tDiv, this.mApp.viewer);
        tInteractor.worldMatrix = this.mArticle.worldTransform;
        tInteractor.position = pPos;
        tInteractor.customCssTransform = "translate(-50%, -50%)";
        tInteractor.onTriggerAction = () => {
            const tRefPoint = Vector3.TransformCoordinates(pPos, this.mArticle.worldTransform);
            const tTool = new DimChangeTool(this.mApp, this.mContext, pBBOrigin, tRefPoint, pDimChange1, pDimChange2, pPlane);
            this.mApp.tools.startTool(tTool);
        };
        this.mInteractors.addInteractor(tInteractor);
    }
    getSortedDimChanges(pDimChanges) {
        return (pDimChanges.sort((a, b) => {
            const tAxisA = DimChangeMode.GetAxis(a);
            const tAxisB = DimChangeMode.GetAxis(b);
            return (compare(tAxisA, tAxisB));
        }));
    }
    getPlane(pDim1, pDim2, pDim3) {
        let tPlane = "xz"; //default
        if (pDim3 != null) {
            //3 axis allowed, 3rd axis defines plane for the other 2 axis
            const tAxis = DimChangeMode.GetAxis(pDim3);
            if (tAxis === "x") {
                tPlane = "yz";
            }
            else if (tAxis === "z") {
                tPlane = "xy";
            }
        }
        else if (pDim2 != null) {
            //2 axis allowed, combine axis to get plane
            tPlane = (DimChangeMode.GetAxis(pDim1) + DimChangeMode.GetAxis(pDim2));
        }
        else {
            //only one axis allowed
            const tAxis = DimChangeMode.GetAxis(pDim1);
            if (tAxis === "y") {
                tPlane = "xy";
            }
        }
        return (tPlane);
    }
}
